import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Mail, MapPin } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Contact = ({ selectedTea, clearSelectedTea }) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Auto-fill message when a tea is selected
  useEffect(() => {
    if (selectedTea) {
      setFormData(prev => ({
        ...prev,
        message: `Hi, I'm interested in learning more about ${selectedTea}. `
      }));
    }
  }, [selectedTea]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: formData.phone ? `Inquiry (Phone: ${formData.phone})` : 'General Inquiry',
          message: formData.message
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit');
      }

      toast({
        title: "Message Sent!",
        description: "Thank you for your inquiry! We'll respond within 24 hours.",
      });
      setFormData({ name: '', email: '', phone: '', message: '' });
      if (clearSelectedTea) clearSelectedTea();
    } catch (error) {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20" style={{ backgroundColor: 'white' }}>
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2
            className="text-4xl md:text-5xl font-bold mb-4"
            style={{ 
              color: '#5e3a1c',
              fontFamily: 'Georgia, serif'
            }}
          >
            Get in Touch
          </h2>
          <p className="text-lg max-w-2xl mx-auto" style={{ color: '#8b5c2f' }}>
            Questions about our brews? We'd love to hear from you
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          <div className="lg:col-span-2">
            <Card className="border-2" style={{ borderColor: '#c2a357' }}>
              <CardHeader>
                <CardTitle style={{ color: '#5e3a1c' }}>Send Us a Message</CardTitle>
                <CardDescription style={{ color: '#8b5c2f' }}>
                  Fill out the form below and we'll get back to you within 24 hours
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" style={{ color: '#5e3a1c' }}>Name</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your name"
                      required
                      className="border-2"
                      style={{ borderColor: '#c2a357' }}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="email" style={{ color: '#5e3a1c' }}>Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      required
                      className="border-2"
                      style={{ borderColor: '#c2a357' }}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="phone" style={{ color: '#5e3a1c' }}>Phone (Optional)</Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+1 (555) 000-0000"
                      className="border-2"
                      style={{ borderColor: '#c2a357' }}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="message" style={{ color: '#5e3a1c' }}>Message</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your inquiry..."
                      rows={5}
                      required
                      className="border-2"
                      style={{ borderColor: '#c2a357' }}
                    />
                  </div>
                  
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full"
                    style={{ backgroundColor: '#8b5c2f', color: '#f5e9d0' }}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card className="border-2" style={{ borderColor: '#c2a357' }}>
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div
                    className="p-3 rounded-full"
                    style={{ backgroundColor: '#f5e9d0' }}
                  >
                    <Mail className="w-5 h-5" style={{ color: '#8b5c2f' }} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold mb-2" style={{ color: '#5e3a1c' }}>Email</h3>
                    <a 
                      href="mailto:MudButtBrewinfo@protonmail.com"
                      className="inline-block px-4 py-2 rounded-md text-sm font-medium hover:opacity-80 transition-opacity break-all"
                      style={{ backgroundColor: '#8b5c2f', color: '#f5e9d0' }}
                    >
                      MudButtBrewinfo@protonmail.com
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2" style={{ borderColor: '#c2a357' }}>
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div
                    className="p-3 rounded-full"
                    style={{ backgroundColor: '#f5e9d0' }}
                  >
                    <MapPin className="w-5 h-5" style={{ color: '#8b5c2f' }} />
                  </div>
                  <div>
                    <h3 className="font-bold mb-1" style={{ color: '#5e3a1c' }}>Location</h3>
                    <p style={{ color: '#8b5c2f' }}>
                      Elma, Washington
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};
